import React, { useState } from 'react';
import { Card, Table, Button, Tag, Space, Typography, Tooltip, Alert, Empty, Modal } from 'antd';
import { ReloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useFailedTransactionsStats, useClearFailedTransactionsCache } from '../../hooks/useQueueManagement';

const { Title, Text } = Typography;
const { confirm } = Modal;

/**
 * Format a timestamp to a human-readable relative time
 * @param date The date to format
 * @param options Options for formatting
 * @param options.addSuffix Whether to add a suffix (e.g., "ago")
 * @returns A string representing the relative time (e.g., "2 minutes ago")
 */
const formatDistanceToNow = (date: Date, options?: { addSuffix?: boolean }): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Default to adding suffix if not specified
  const addSuffix = options?.addSuffix !== false;
  const suffix = addSuffix ? ' ago' : '';

  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds === 1 ? '' : 's'}${suffix}`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'}${suffix}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'}${suffix}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays === 1 ? '' : 's'}${suffix}`;
};

// Define types for the failed transactions data
interface FailedTransaction {
  id: string;
  status: string;
  error: string;
  timestamp: number;
  ttl: number;
}

interface FailedTransactionsStats {
  count: number;
  byStatus: Record<string, number>;
  transactions: FailedTransaction[];
}

interface FailedTransactionsResponse {
  success: boolean;
  stats: FailedTransactionsStats;
  error?: string;
}

/**
 * Component for displaying and managing failed transactions cache
 */
const FailedTransactionsPanel: React.FC = () => {
  // Translation hooks
  const { t } = useTranslationWithNamespace('queue');
  const { t: commonT } = useTranslationWithNamespace('common');

  // State
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Queries and mutations
  const { data, isLoading, refetch } = useFailedTransactionsStats<FailedTransactionsResponse>();
  const clearCacheMutation = useClearFailedTransactionsCache();

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Handle clear cache
  const handleClearCache = () => {
    confirm({
      title: t('failedTransactions.clearConfirmTitle', 'Clear Failed Transactions Cache?'),
      icon: <ExclamationCircleOutlined />,
      content: t('failedTransactions.clearConfirmContent', 'This will remove all transactions from the failed transactions cache, allowing them to be processed again. Are you sure?'),
      okText: commonT('yes', 'Yes'),
      okType: 'danger',
      cancelText: commonT('no', 'No'),
      onOk: async () => {
        try {
          await clearCacheMutation.mutateAsync();
        } catch (error) {
          console.error('Error clearing failed transactions cache:', error);
        }
      }
    });
  };

  // Table columns
  const columns = [
    {
      title: t('failedTransactions.id', 'Transaction ID'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Tooltip title={text}>
          <Text copyable={{ text }}>{text.substring(0, 10)}...</Text>
        </Tooltip>
      )
    },
    {
      title: t('failedTransactions.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (text: string) => (
        <Tag color={text === 'Requested' ? 'blue' : text === 'Approved' ? 'green' : text === 'Failed' ? 'red' : 'default'}>
          {text || 'Unknown'}
        </Tag>
      )
    },
    {
      title: t('failedTransactions.error', 'Error'),
      dataIndex: 'error',
      key: 'error',
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text ? (text.length > 50 ? `${text.substring(0, 50)}...` : text) : 'Unknown error'}</Text>
        </Tooltip>
      )
    },
    {
      title: t('failedTransactions.timestamp', 'Failed At'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: number) => (
        <Tooltip title={new Date(timestamp).toLocaleString()}>
          {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
        </Tooltip>
      )
    },
    {
      title: t('failedTransactions.ttl', 'Retry In'),
      dataIndex: 'ttl',
      key: 'ttl',
      render: (ttl: number) => (
        <Text>{ttl > 0 ? `${ttl} seconds` : 'Soon'}</Text>
      )
    }
  ];

  // Render
  return (
    <Card
      title={
        <Space>
          <Title level={5}>{t('failedTransactions.title', 'Failed Transactions Cache')}</Title>
          {data?.stats?.count && data.stats.count > 0 && (
            <Tag color="red">{data.stats.count}</Tag>
          )}
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isRefreshing}
            type="text"
          >
            {t('refresh', 'Refresh')}
          </Button>
          <Button
            icon={<DeleteOutlined />}
            onClick={handleClearCache}
            loading={clearCacheMutation.isPending}
            type="primary"
            danger
            disabled={!data?.stats?.count || data.stats.count === 0}
          >
            {t('failedTransactions.clearCache', 'Clear Cache')}
          </Button>
        </Space>
      }
    >
      {!data?.stats?.count || data.stats.count === 0 ? (
        <Empty description={t('failedTransactions.empty', 'No failed transactions in cache')} />
      ) : (
        <>
          {data?.stats?.byStatus && Object.keys(data.stats.byStatus).length > 0 && (
            <Space style={{ marginBottom: 16 }}>
              {Object.entries(data.stats.byStatus).map(([status, count]) => (
                <Tag key={status} color={status === 'Requested' ? 'blue' : status === 'Approved' ? 'green' : status === 'Failed' ? 'red' : 'default'}>
                  {`${status}: ${count}`}
                </Tag>
              ))}
            </Space>
          )}

          <Alert
            message={t('failedTransactions.info', 'These transactions have recently failed to enqueue and will be retried automatically after the cooldown period.')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={data?.stats?.transactions ?? []}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 5 }}
            size="small"
          />
        </>
      )}
    </Card>
  );
};

export default FailedTransactionsPanel;
